// The one place Video Intelligence prices live.
//
// Rates are USD per minute of analyzed video, above the free tier, transcribed
// from `admin/VideoAIPricing.md`. Nothing under `functions/` carries a copy and
// no cost is stored on any document: `videoAnalysis/{id}` keeps `durationSec`
// and the features actually requested, and cost is derived here at the point of
// display. Two tables that could disagree about money is not a trade worth
// making to save a multiplication.

export const VIDEO_AI_FEATURES = [
  { key: 'explicitContent', apiEnum: 'EXPLICIT_CONTENT_DETECTION', labelKey: 'videoAI.featureExplicit', pricePerMinute: 0.10 },
  { key: 'face', apiEnum: 'FACE_DETECTION', labelKey: 'videoAI.featureFace', pricePerMinute: 0.10 },
  { key: 'label', apiEnum: 'LABEL_DETECTION', labelKey: 'videoAI.featureLabel', pricePerMinute: 0.10 },
  { key: 'logo', apiEnum: 'LOGO_RECOGNITION', labelKey: 'videoAI.featureLogo', pricePerMinute: 0.15 },
  { key: 'objectTracking', apiEnum: 'OBJECT_TRACKING', labelKey: 'videoAI.featureObject', pricePerMinute: 0.15 },
  { key: 'person', apiEnum: 'PERSON_DETECTION', labelKey: 'videoAI.featurePerson', pricePerMinute: 0.10 },
  { key: 'speechTranscription', apiEnum: 'SPEECH_TRANSCRIPTION', labelKey: 'videoAI.featureSpeech', pricePerMinute: 0.048 },
  { key: 'text', apiEnum: 'TEXT_DETECTION', labelKey: 'videoAI.featureText', pricePerMinute: 0.15 },
]

// Google gives every project this many minutes free each month, across
// features. Shown next to the estimate so the number is not read as a bill.
export const FREE_TIER_MINUTES = 1000

// Explicit content is scored per frame on a five-level scale. Only the top
// three are offered as a reject threshold; rejecting on UNLIKELY would reject
// nearly everything.
export const EXPLICIT_THRESHOLDS = ['POSSIBLE', 'LIKELY', 'VERY_LIKELY']
export const DEFAULT_EXPLICIT_THRESHOLD = 'LIKELY'

export const DEFAULT_VIDEO_AI_CONFIG = {
  enabled: false,
  features: Object.fromEntries(VIDEO_AI_FEATURES.map((f) => [f.key, false])),
  aiModeration: false,
  explicitThreshold: DEFAULT_EXPLICIT_THRESHOLD,
}

const PRICES = Object.fromEntries(VIDEO_AI_FEATURES.map((f) => [f.key, f.pricePerMinute]))

/** The checked feature keys of a config, in VIDEO_AI_FEATURES order. */
export function enabledFeatureKeys(config) {
  const features = (config && config.features) || {}
  return VIDEO_AI_FEATURES.filter((f) => features[f.key] === true).map((f) => f.key)
}

/** USD per minute of video for a set of feature keys. Unknown keys cost 0. */
export function pricePerMinute(keys) {
  return (keys || []).reduce((sum, key) => sum + (PRICES[key] || 0), 0)
}

/** USD per second of video for a set of feature keys. */
export function estimateCostPerSecond(keys) {
  return pricePerMinute(keys) / 60
}

/** USD to analyze `durationSec` seconds of video with those features. */
export function estimateCostUsd(keys, durationSec) {
  return estimateCostPerSecond(keys) * (Number(durationSec) || 0)
}
